import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { lt } from "./lt";
import { en } from "./en";
import { pl } from "./pl";

export type Language = "lt" | "en" | "pl";
export type Market = "lt" | "intl";
export type Dictionary = typeof lt;
type RuntimeDictionary = Record<string, unknown> & { routes: Record<keyof Dictionary["routes"], string> };

type TParams = Record<string, string | number>;

type LangContextValue = {
  lang: Language;
  market: Market;
  dictionary: RuntimeDictionary;
  setLanguage: (next: Language) => void;
  t: (key: string, params?: TParams) => string;
  route: (key: keyof Dictionary["routes"]) => string;
  canonicalBase: string;
};

const dictionaries: Record<Language, RuntimeDictionary> = {
  lt: lt as unknown as RuntimeDictionary,
  en: en as unknown as RuntimeDictionary,
  pl: pl as unknown as RuntimeDictionary,
};
const STORAGE_KEY = "ld_language";
const MARKET_KEY = "ld_market";

const isBrowser = typeof window !== "undefined";

function normalizeLang(value: string | null | undefined): Language | null {
  if (value === "lt" || value === "en" || value === "pl") return value;
  return null;
}

// zemelapis.com — Metų žemėlapio landingas (visų Žemėlapių namai); ta pati LT rinka, kita pradžios tema
export function isZemelapisHost(hostname: string = isBrowser ? window.location.hostname : ""): boolean {
  const h = hostname.toLowerCase();
  return h === "zemelapis.com" || h.endsWith(".zemelapis.com");
}

function domainMarket(hostname: string): Market | null {
  const host = hostname.toLowerCase();
  if (host === "humanmostly.com" || host.endsWith(".humanmostly.com")) return "intl";
  if (isZemelapisHost(host)) return "lt";
  if (host === "laimesdieta.lt" || host === "www.laimesdieta.lt" || host === "laimespamokos.lt" || host === "www.laimespamokos.lt" || host === "laimesmokykla.lt" || host === "www.laimesmokykla.lt" || host === "laiminginamai.lt") return "lt";
  return null;
}

function resolveMarket(search: URLSearchParams): Market {
  if (!isBrowser) return "lt";
  const fromDomain = domainMarket(window.location.hostname);
  if (fromDomain) return fromDomain;

  // Preview/localhost: ?market= is a testing switch, so remember it for the
  // rest of the visit — otherwise every internal link falls back to LT.
  const fromUrl = search.get("market");
  if (fromUrl === "intl" || fromUrl === "lt") {
    window.localStorage.setItem(MARKET_KEY, fromUrl);
    return fromUrl;
  }
  return window.localStorage.getItem(MARKET_KEY) === "intl" ? "intl" : "lt";
}

function resolveLanguage(search: URLSearchParams, market: Market): Language {
  const fromUrl = normalizeLang(search.get("lang"));

  // The Lithuanian market is always served in Lithuanian. Stored preferences
  // and browser locales must never turn laimesdieta.lt into an English site.
  if (market === "lt") return "lt";

  if (fromUrl) return fromUrl;

  if (isBrowser) {
    const stored = normalizeLang(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;

    const host = window.location.hostname.toLowerCase();
    if (host === "humanmostly.com" || host.endsWith(".humanmostly.com")) return "en";
    if (host.includes("lovable") || host === "localhost") return "en";

    const browserLang = window.navigator.language.toLowerCase();
    if (browserLang.startsWith("en")) return "en";
  }

  return "en";
}

function lookup(dictionary: RuntimeDictionary, key: string): unknown {
  return key.split(".").reduce<unknown>((acc, part) => {
    if (!acc || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[part];
  }, dictionary);
}

function interpolate(value: string, params?: TParams): string {
  if (!params) return value;
  return Object.entries(params).reduce((acc, [key, replacement]) => acc.split(`{${key}}`).join(String(replacement)), value);
}

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const market = useMemo(() => resolveMarket(search), [search]);
  const [lang, setLang] = useState<Language>(() => resolveLanguage(search, market));

  useEffect(() => {
    const next = resolveLanguage(search, market);
    setLang(next);
  }, [search, market]);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const dictionary = dictionaries[lang] ?? dictionaries.en;
  const canonicalBase = market === "intl" ? "https://humanmostly.com" : "https://laimesdieta.lt";

  const setLanguage = useCallback(
    (next: Language) => {
      const params = new URLSearchParams(location.search);
      const onLtDomain = isBrowser && domainMarket(window.location.hostname) === "lt";
      const onIntlDomain = isBrowser && domainMarket(window.location.hostname) === "intl";

      // Crossing markets: the Lithuanian market is always Lithuanian, so
      // choosing English means going to the international site (and back).
      if (market === "lt" && next !== "lt") {
        window.localStorage.setItem(STORAGE_KEY, next);
        if (onLtDomain) {
          window.location.href = `https://humanmostly.com/?lang=${next}`;
          return;
        }
        params.set("market", "intl");
        params.set("lang", next);
        navigate(`/?${params.toString()}`, { replace: true });
        return;
      }

      if (market === "intl" && next === "lt") {
        window.localStorage.setItem(STORAGE_KEY, next);
        if (onIntlDomain) {
          window.location.href = "https://laimesdieta.lt/";
          return;
        }
        window.localStorage.setItem(MARKET_KEY, "lt");
        params.delete("market");
        params.delete("lang");
        const query = params.toString();
        navigate(query ? `/?${query}` : "/", { replace: true });
        return;
      }

      setLang(next);
      window.localStorage.setItem(STORAGE_KEY, next);
      params.set("lang", next);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [location.pathname, location.search, market, navigate],
  );

  const t = useCallback(
    (key: string, params?: TParams) => {
      const value = lookup(dictionary, key) ?? lookup(dictionaries.en, key) ?? key;
      return typeof value === "string" ? interpolate(value, params) : key;
    },
    [dictionary],
  );

  const route = useCallback((key: keyof Dictionary["routes"]) => dictionary.routes[key], [dictionary]);

  const value = useMemo(
    () => ({ lang, market, dictionary, setLanguage, t, route, canonicalBase }),
    [lang, market, dictionary, setLanguage, t, route, canonicalBase],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
}

export function useT() {
  return useLang().t;
}

export function useMarket() {
  return useLang().market;
}

export function useLocalizedRoute() {
  return useLang().route;
}
